/* eslint-disable no-warning-comments */
// Call plugin.
import { normalizeSipUri } from '../../utils/normalization'
import { getOptions } from '../../interfaceNew/selectors'

// Other plugins.
import { getDomain, getRequestInfo } from '../../../auth/interface/selectors'
import { logManager } from '../../../logs'

// Helpers.
import request from '../../../request/effects'
import BasicError from '../../../errors'
import { platforms } from '../../../constants'
import { getWebsocketId } from '../utils'

// Libraries.
import { call, select } from 'redux-saga/effects'

/**
 * Creates a webRTC session on the server. Link-specific signalling function.
 *
 * This saga "starts a call" between the current user and another, specified
 *    user.
 * Assumptions:
 *    1. The current user is authenticated.
 * Responsibilities:
 *    1. Format parameters as needed for signalling.
 *    2. Perform the REST request.
 *    3. Return the response, formatted.
 * @method createSession
 * @param  {Object} callInfo
 * @param  {Object} callInfo.id The SDK's ID for the Call.
 * @param  {string} callInfo.participantAddress The user to receive the call.
 * @param  {string} callInfo.offer The local SDP offer to begin negotiation.
 * @param  {boolean} [callInfo.isAnonymous] Flag indicating whether the call is anonymous or not.
 * @param  {string} [callInfo.account] An account token used by the request if it is an anonymous call.
 * @param  {string} [callInfo.from] A from token used by the request if it is an anonymous call.
 * @param  {string} [callInfo.displayName] A custom display name to use. Not supported in all environments and may use default display name.
 * @param  {Array<CustomParameter>} [callInfo.customParameters] Custom SIP header parameters for the SIP backend.
 * @param  {Array<CustomBody>} [callInfo.customBodies] Custom SIP bodies for the SIP backend.
 * @return {Object} response Signalling response.
 * @return {string} [response.wrtcsSessionId] ID that the server uses to track this call.
 * @return {Object} [response.error] An error object, if signalling failed.
 */
export function * createSession (callInfo) {
  const log = logManager.getLogger('CALL', callInfo.id)
  log.info('Creating call session on server-side.')

  // Collect the information needed to make the request.
  const requestInfo = yield select(getRequestInfo, platforms.LINK)
  const callOptions = yield select(getOptions)

  // requestInfo.username isn't normalized and link requires a normalized
  // address to make a call.  Since we also use username to build the request
  // url, we need it both normalized and straight up.  So I'm normalizing it and
  // putting it on the requestInfo object under a new property name 'fromAddress'
  if (requestInfo.username) {
    const domain = yield select(getDomain)
    // Normalize callee addresses
    callInfo.fromAddress = normalizeSipUri(requestInfo.username, domain)
    log.debug('Caller address normalized to: ', callInfo.fromAddress)
  }

  // Default backend configuration does not support this
  //  so remote side might see default display name instead.
  // Backend needs to be configured to allow a custom display name.
  if (callInfo.displayName) {
    callInfo.fromAddress = `${callInfo.displayName}<${callInfo.fromAddress}>`
    callInfo.from = `${callInfo.displayName}<${callInfo.from}>`
  }

  const options = {
    method: 'POST'
  }
  const requestBody = {
    type: 'callStart',
    to: callInfo.participantAddress,
    sdp: callInfo.offer,
    supported: [],
    customParameters: callInfo.customParameters,
    customBodies: callInfo.customBodies
  }

  if (callOptions.earlyMedia) {
    requestBody.supported.push('earlymedia')
  }

  /*
   * If there are no (extra) supported items, remove the field from the request
   *    body. This is to avoid SPiDR rejecting the body when it is an empty
   *    array (which happens for anonymous calls).
   */
  if (requestBody.supported.length === 0) {
    delete requestBody.supported
  }

  if (callInfo.isAnonymous) {
    const getWebsocketIdResult = yield call(getWebsocketId)
    if (getWebsocketIdResult.error) {
      return { error: getWebsocketIdResult.error }
    }
    options.body = JSON.stringify({
      callMeRequest: {
        ...requestBody,
        from: callInfo.from,
        notifyChannelId: getWebsocketIdResult.websocketId
      }
    })
  } else {
    options.body = JSON.stringify({
      callControlRequest: {
        ...requestBody,
        from: callInfo.fromAddress
      }
    })
  }

  const response = yield call(linkCallRequest, requestInfo, { ...options, ...callInfo })
  const responseBodyType = callInfo.isAnonymous ? 'callMeResponse' : 'callControlResponse'
  if (response.error) {
    log.debug('Failed to create Call session server-side.', response.error)
    return {
      error: response.error
    }
  } else {
    const wrtcsSessionId = response[responseBodyType].sessionData
    log.debug('Call session created server-side.', { wrtcsSessionId })

    return {
      error: false,
      wrtcsSessionId
    }
  }
}

/**
 * Updates a webRTC session on the server with a new state.
 * Link-specific signaling function.
 *
 * The saga updates the server's session to be in "ringing" state.
 * Assumptions:
 *    1. The current user is authenticated.
 * Responsibilities:
 *    1. Format parameters as needed for signalling.
 *    2. Perform the REST request.
 *    3. Return the response, formatted.
 * @method updateCallRinging
 * @param  {Object} callInfo
 * @param  {Object} callInfo.id The SDK's ID for the Call.
 * @param  {string} callInfo.wrtcsSessionId ID that the server uses to identify the session.
 * @param  {string} callInfo.isAnonymous    Whether the call is an anonymous call.
 * @return {Object} response Signalling response.
 * @return {Object} [response.error] An error object, if signalling failed.
 */
export function * updateCallRinging (callInfo) {
  const log = logManager.getLogger('CALL', callInfo.id)
  log.info('Updating call session as ringing on server-side.')

  // Collect the information needed to make the request.
  const requestInfo = yield select(getRequestInfo, platforms.LINK)

  const options = {
    method: 'PUT'
  }

  options.body = JSON.stringify({
    callControlRequest: {
      type: 'ringing'
    }
  })

  const response = yield call(linkCallRequest, requestInfo, { ...options, ...callInfo })

  if (response.error) {
    log.debug('Failed to update call session as ringing server-side.', response.error)
    return {
      error: response.error
    }
  } else {
    log.debug('Call session updated as ringing server-side.', {
      wrtcsSessionId: callInfo.wrtcsSessionId
    })
    return {
      error: false
    }
  }
}

/**
 * Sends the (new?) custom parameters of the call to the webRTC session on the server.
 * @method updateCustomParameters
 * @param {Object} callInfo
 * @param {string} callInfo.wrtcsSessionId    ID that the server uses to identify the session.
 * @param {string} callInfo.isAnonymous       Whether the call is an anonymous call
 * @param {Array<CustomParameter>} callInfo.customParameters  The custom parameters
 * @return {Object} response Signaling response.
 */
export function * updateCustomParameters (callInfo) {
  // Collect the information needed to make the request.
  const requestInfo = yield select(getRequestInfo, platforms.LINK)

  const options = {
    method: 'PUT'
  }

  const bodyType = callInfo.isAnonymous ? 'callMeRequest' : 'callControlRequest'

  options.body = JSON.stringify({
    [bodyType]: {
      type: 'sendCustomHeaders',
      customParameters: callInfo.customParameters
    }
  })

  const response = yield call(linkCallRequest, requestInfo, { ...options, ...callInfo })

  return {
    error: response.error ? response.error : false
  }
}

/**
 * REST DELETE request to send webRTC session call end requests.
 * @method endSession
 * @param  {Object} requestInfo
 * @param  {Object} callInfo
 * @param  {string} callInfo.wrtcsSessionId ID that the server uses to identify the session.
 * @param  {boolean} callInfo.isAnonymous Whether the call is anonymous or not
 * @param  {string} [callInfo.account] An account token used by the request if it is an anonymous call.
 * @return {Object} response Signalling response.
 * @return {Object} [response.error] An BasicError object, if signalling failed.
 */
export function * endSession (callInfo) {
  const requestInfo = yield select(getRequestInfo, platforms.LINK)

  const options = {
    method: 'DELETE',
    responseType: 'none'
  }

  const response = yield call(linkCallRequest, requestInfo, { ...options, ...callInfo })

  if (response.error) {
    return {
      error: response.error
    }
  } else {
    return {
      error: false
    }
  }
}

/**
 * Updates a webRTC session on the server with an answer.
 * Link-specific signaling function.
 *
 * This saga "answers a call" from another user.
 * Assumptions:
 *    1. The current user is authenticated.
 * Responsibilities:
 *    1. Format parameters as needed for signalling.
 *    2. Perform the REST request.
 *    3. Return the response, formatted.
 * @method answerSession
 * @param  {Object} callInfo
 * @param  {string} callInfo.wrtcsSessionId ID that the server uses to identify the session.
 * @param  {string} callInfo.answer The local SDP to complete negotiation. This may be an offer is performing slow start answer.
 * @param  {Array<CustomParameter>} callInfo.customParameters The custom parameters of the call.
 * @param  {Array<CustomBody>} callInfo.customBodies The custom bodies of the call.
 * @return {Object} response Signalling response.
 * @return {Object} [response.error] An error object, if signalling failed.
 */
export function * answerSession (callInfo) {
  const log = logManager.getLogger('CALL', callInfo.id)
  log.info('Answering call session on server-side.')

  // Collect the information needed to make the request.
  const requestInfo = yield select(getRequestInfo, platforms.LINK)

  const options = {
    method: 'PUT'
  }

  options.body = JSON.stringify({
    callControlRequest: {
      type: 'callAnswer',
      sdp: callInfo.answer,
      customParameters: callInfo.customParameters,
      customBodies: callInfo.customBodies
    }
  })

  const response = yield call(linkCallRequest, requestInfo, { ...options, ...callInfo })

  if (response.error) {
    log.debug('Failed to answer Call session server-side.', response.error)
    return {
      error: response.error
    }
  } else {
    log.debug('Call session answered server-side.', { wrtcsSessionId: callInfo.wrtcsSessionId })
    return {
      error: false
    }
  }
}

/**
 * Rejects an incoming session.
 * Link-specific signaling function.
 *
 * This saga "rejects" the server session.
 * Assumptions:
 *    1. The current user is authenticated.
 *    2. There is an incoming session.
 * Responsibilities:
 *    1. Format parameters as needed for signalling.
 *    2. Perform the REST request.
 *    3. Return the response, formatted.
 * REST request to reject a webRTC session.
 * @method rejectSession
 * @param  {Object} callInfo
 * @param  {string} callInfo.wrtcsSessionId The ID the backend uses to track the session.
 * @return {Object} response object from the server.
 * @return {Object} [response.error] An error object, if signalling failed.
 */
export function * rejectSession (callInfo) {
  const requestInfo = yield select(getRequestInfo, platforms.LINK)

  const options = {
    method: 'POST'
  }

  options.endUrl = 'calldisposition'

  options.body = JSON.stringify({
    callDispositionRequest: {
      action: 'reject',
      sessionData: callInfo.wrtcsSessionId
    }
  })

  const response = yield call(linkCallRequest, requestInfo, options)

  if (response.error) {
    return {
      error: response.error
    }
  } else {
    return {
      error: false
    }
  }
}

/**
 * Forwards an incoming session to another destination address.
 * Link-specific signaling function.
 *
 * This saga "forwards" the server session to a specified destination address.
 * Assumptions:
 *    1. The current user is authenticated.
 *    2. There is an incoming session.
 * Responsibilities:
 *    1. Format parameters as needed for signalling.
 *    2. Perform the REST request.
 *    3. Return the response, formatted.
 * @method forwardSession
 * @param  {Object} callInfo
 * @param  {string} callInfo.wrtcsSessionId The ID the backend uses to track the session.
 * @param  {string} callInfo.address The address to forward the session to.
 * @return {Object} response object from the server.
 * @return {Object} [response.error] An error object, if signalling failed.
 */
export function * forwardSession (callInfo) {
  const requestInfo = yield select(getRequestInfo, platforms.LINK)

  const options = {
    method: 'POST'
  }

  options.endUrl = 'calldisposition'

  options.body = JSON.stringify({
    callDispositionRequest: {
      action: 'forward',
      sessionData: callInfo.wrtcsSessionId,
      address: callInfo.address
    }
  })

  const response = yield call(linkCallRequest, requestInfo, options)

  if (response.error) {
    return {
      error: response.error
    }
  } else {
    return {
      error: false
    }
  }
}

/**
 * Transfers ongoing sessions into one another.
 * Link-specific signaling function.
 *
 * This saga "transfers" the server sessions.
 * Assumptions:
 *    1. The current user is authenticated.
 * Responsibilities:
 *    1. Format parameters as needed for signalling.
 *    2. Perform the REST request.
 *    3. Return the response, formatted.
 * @method consultativeTransferSessions
 * @param  {Object} callInfo
 * @param  {string} callInfo.wrtcsSessionId The ID the backend uses to track the session.
 * @param  {string} callInfo.otherWrtcsSessionId The ID the backend uses to track the other session.
 * @param  {string} callInfo.destination The address of the other session to transfer to.
 * @return {Object} response object from the server.
 * @return {Object} [response.error] An error object, if signalling failed.
 * @return {string} [response.newWrtcsSessionId] The back-end session id, if signalling succeeded.
 */
export function * consultativeTransferSessions (callInfo) {
  const requestInfo = yield select(getRequestInfo, platforms.LINK)

  const options = {
    method: 'PUT'
  }

  options.body = JSON.stringify({
    callControlRequest: {
      type: 'transfer',
      sessionData: callInfo.otherWrtcsSessionId,
      address: callInfo.destination
    }
  })

  const response = yield call(linkCallRequest, requestInfo, { ...options, ...callInfo })

  if (response.error) {
    return {
      error: response.error
    }
  } else {
    return {
      error: false
    }
  }
}

/**
 * Direct Transfers an incoming session to another destination address.
 * Link-specific signaling function.
 *
 * This saga "direct transfers" the server session to a specified destination address.
 * Assumptions:
 *    1. The current user is authenticated.
 * Responsibilities:
 *    1. Format parameters as needed for signalling.
 *    2. Perform the REST request.
 *    3. Return the response, formatted.
 * @method directTransferSession
 * @param  {Object} callInfo
 * @param  {string} callInfo.wrtcsSessionId The ID the backend uses to track the session.
 * @param  {string} callInfo.address The address to forward the session to.
 * @return {Object} response object from the server.
 * @return {Object} [response.error] An error object, if signalling failed.
 */
export function * directTransferSession (callInfo) {
  const requestInfo = yield select(getRequestInfo, platforms.LINK)

  const options = {
    method: 'PUT'
  }

  options.body = JSON.stringify({
    callControlRequest: {
      type: 'transfer',
      address: callInfo.address
    }
  })

  const response = yield call(linkCallRequest, requestInfo, { ...options, ...callInfo })

  if (response.error) {
    return {
      error: response.error
    }
  } else {
    return {
      error: false
    }
  }
}

/**
 * Joins ongoing sessions into a new session.
 * Link-specific signaling function.
 *
 * This saga "joins" the server sessions.
 * Assumptions:
 *    1. The current user is authenticated.
 * Responsibilities:
 *    1. Format parameters as needed for signalling.
 *    2. Perform the REST request.
 *    3. Return the response, formatted.
 * @method joinSessions
 * @param  {Object} callInfo
 * @param  {string} callInfo.wrtcsSessionId The ID the backend uses to track the session.
 * @param  {string} callInfo.otherWrtcsSessionId
 * @param  {string} callInfo.sdp The new sdp to use.
 * @param  {Array<CustomParameter>} callInfo.customParameters The custom parameters of the current call.
 * @param  {Array<CustomBody>} callInfo.customBodies The custom bodies of the call.
 * @return {Object} response object from the server.
 * @return {Object} [response.error] An error object, if signalling failed.
 * @return {string} [response.newWrtcsSessionId] The back-end session id, if signalling succeeded.
 */
export function * joinSessions (callInfo) {
  const requestInfo = yield select(getRequestInfo, platforms.LINK)

  const options = {
    method: 'POST'
  }

  options.body = JSON.stringify({
    callControlRequest: {
      type: 'join',
      firstSessionData: callInfo.wrtcsSessionId,
      secondSessionData: callInfo.otherWrtcsSessionId,
      sdp: callInfo.sdp,
      customParameters: callInfo.customParameters
    }
  })

  const response = yield call(linkCallRequest, requestInfo, options)

  if (response.error) {
    return {
      error: response.error
    }
  } else {
    return {
      error: false,
      newWrtcsSessionId: response.callControlResponse.sessionData
    }
  }
}

/**
 * Updates an existing webRTC session on the server.
 * Link-specific signalling function.
 *
 * This saga "updates a session" between the current user and another, specified
 *    user.
 * Assumptions:
 *    1. The current user is authenticated.
 * Responsibilities:
 *    1. Format parameters as needed for signalling.
 *    2. Perform the REST request.
 *    3. Return the response, formatted.
 * @method updateSession
 * @param  {Object} callInfo
 * @param  {string} callInfo.wrtcsSessionId The ID the backend uses to track the session.
 * @param  {string} callInfo.offer The local SDP offer to begin negotiation.
 * @param  {boolean} [callInfo.isAnonymous] Flag indicating whether the call is anonymous or not.
 * @param  {string}  [callInfo.account] An account token used by the request if it is an anonymous call.
 * @param  {Array}   [callInfo.customParameters] The custom parameters of the call.
 * @param  {Array}   [callInfo.customBodies] The custom bodies of the call.
 * @return {Object} response Signalling response.
 * @return {Object} response.error An error object, if signalling failed.
 */
export function * updateSession (callInfo) {
  // Collect the information needed to make the request.
  const requestInfo = yield select(getRequestInfo, platforms.LINK)

  const options = {
    method: 'PUT'
  }
  const bodyType = callInfo.isAnonymous ? 'callMeRequest' : 'callControlRequest'

  options.body = JSON.stringify({
    [bodyType]: {
      type: 'startCallUpdate',
      sdp: callInfo.offer,
      customParameters: callInfo.customParameters,
      customBodies: callInfo.customBodies
    }
  })

  const response = yield call(linkCallRequest, requestInfo, { ...options, ...callInfo })

  if (response.error) {
    return {
      error: response.error
    }
  } else {
    return {
      error: false
    }
  }
}

/**
 * Gets the status of a session from the server.
 * Link-specific signaling function.
 *
 * Assumptions:
 *    1. The current user is authenticated.
 *    2. Signalling server has WebRTC Gateway 4.7.1+
 * Responsibilities:
 *    1. Format parameters as needed for signalling.
 *    2. Perform the REST request.
 *    3. Return the response, formatted.
 * @method getSessions
 * @param  {Object} callInfo
 * @param  {Object} callInfo.wrtcsSessionId The ID the backend uses to track the session.
 * @param  {boolean} [callInfo.isAnonymous] Flag indicating whether the call is anonymous or not.
 * @param  {string} [callInfo.account] An account token used by the request if it is an anonymous call.
 * @return {Object} response Signalling response.
 * @return {Object} response.error An error object, if signalling failed.
 */
export function * getSession (callInfo) {
  // Collect the information needed to make the request.
  const requestInfo = yield select(getRequestInfo, platforms.LINK)

  const options = { method: 'GET' }

  const response = yield call(linkCallRequest, requestInfo, { ...options, ...callInfo })

  if (response.error) {
    return {
      error: response.error
    }
  } else {
    return {
      error: false,
      state: response.callControlResponse.state
    }
  }
}

/**
 * Updates a webRTC session on the server to ensure it's state.
 * Link-specific signaling function.
 *
 * This saga "audits" the server session to:
 *    1. notify the server that the call is still on-going locally, and
 *    2. ensure that the server is still handling the server session.
 * Assumptions:
 *    1. The current user is authenticated.
 * Responsibilities:
 *    1. Format parameters as needed for signalling.
 *    2. Perform the REST request.
 *    3. Return the response, formatted.
 * @method auditCall
 * @param  {Object} callInfo
 * @param  {Object} callInfo.wrtcsSessionId The ID the backend uses to track the session.
 * @param  {boolean} [callInfo.isAnonymous] Flag indicating whether the call is anonymous or not.
 * @param  {string} [callInfo.account] An account token used by the request if it is an anonymous call.
 * @return {Object} response Signalling response.
 * @return {Object} response.error An error object, if signalling failed.
 */
export function * auditCall (callInfo) {
  // Collect the information needed to make the request.
  const requestInfo = yield select(getRequestInfo, platforms.LINK)

  const options = { method: 'PUT' }
  const bodyType = callInfo.isAnonymous ? 'callMeRequest' : 'callControlRequest'

  options.body = JSON.stringify({
    [bodyType]: {
      type: 'audit'
    }
  })

  const response = yield call(linkCallRequest, requestInfo, { ...options, ...callInfo })

  if (response.error) {
    return {
      error: response.error,
      // Indicate whether the error was that the session doesn't exist
      //    anymore (and audits should stop) or if it was an unknown error.
      // 42 == RESOURCE_DOES_NOT_EXIST
      status: response.error.code === 42 ? 'Closed' : 'Retry'
    }
  } else {
    return {
      error: false,
      status: 'Connected'
    }
  }
}

/**
 * Responds to a received remote offer to update the webRTC session.
 * Link-specific signaling function.
 *
 * This saga responds to a "call update" request as part of renegotiation. This
 *    updates the server session with an answer SDP, which in turn should notify
 *    the remote end of the session with the response.
 * Assumptions:
 *    1. The current user is authenticated.
 * Responsibilities:
 *    1. Perform the REST request.
 *    2. Return the response, formatted.
 * @method updateSessionResponse
 * @param  {Object} callInfo
 * @param  {Object} callInfo.wrtcsSessionId The ID the backend uses to track the session.
 * @param  {string} callInfo.answer The local SDP to complete renegotiation. This may be an offer if performing slow start.
 * @param  {boolean} [callInfo.isAnonymous] Flag indicating whether the call is anonymous or not.
 * @param  {string} [callInfo.account] An account token used by the request if it is an anonymous call.
 * @return {Object} response Signalling response.
 * @return {Object} response.error An error object, if signalling failed.
 */
export function * updateSessionResponse (callInfo) {
  // Collect the information needed to make the request.
  const requestInfo = yield select(getRequestInfo, platforms.LINK)

  const options = {
    method: 'PUT'
  }
  const bodyType = callInfo.isAnonymous && callInfo.account ? 'callMeRequest' : 'callControlRequest'

  options.body = JSON.stringify({
    [bodyType]: {
      type: 'respondCallUpdate',
      sdp: callInfo.answer,
      customParameters: callInfo.customParameters,
      customBodies: callInfo.customBodies
    }
  })

  const response = yield call(linkCallRequest, requestInfo, { ...options, ...callInfo })

  if (response.error) {
    return {
      error: response.error
    }
  } else {
    return {
      error: false
    }
  }
}

export function * linkCallRequest (requestInfo, options) {
  options.url = generateCallUrl(requestInfo, options)

  const response = yield request(options)

  if (response.error) {
    return {
      error: handleLinkCallRequestError(response)
    }
  } else {
    return response.payload.body
  }
}

function handleLinkCallRequestError (response) {
  function getMessage (code) {
    switch (code) {
      case 5:
        return 'Request failed: Invalid or missing request body'
      case 26:
        return 'Request failed: Internal Server Error'
      case 1004:
        return 'Request failed: Bad request'
      default:
        return 'Request failed: Unknown error'
    }
  }

  let message, code

  if (response && response.payload) {
    if (response.payload.body) {
      if ('callControlResponse' in response.payload.body) {
        // The response includes a body with an error message.
        const { callControlResponse } = response.payload.body
        message = getMessage(callControlResponse.statusCode)
        // TODO: Do we want to wrap the backend's error codes?
        code = callControlResponse.statusCode
      } else {
        // As serviceException details not available, we will capture general error information
        message = response.payload.body.message
        code = response.payload.result.code
      }
    } else if (response.payload.result) {
      message = `Request failed: ${response.payload.result.message}`
      code = response.payload.result.code
    }
  } else {
    message = 'Request failed: Unknown error.'
  }

  return new BasicError({ message, code })
}

/**
 * Generates the call url for the rest request based on the options provided.
 * @param {Object} requestInfo Object containing request information
 * @param {Object} options
 * @param {boolean} [options.isAnonymous] Flag indicating whether the call is anonymous or not.
 * @param {string} [options.account] An account token used by the request if it is an anonymous call.
 * @param {string} [options.wrtcsSessionId] The ID the backend uses to track the session.
 * @param {string} [options.endUrl] The end url to append to the main url.
 */
function generateCallUrl (requestInfo, options) {
  // Generic user information.
  const { baseURL, version, username } = requestInfo
  // Call specific information.
  const { isAnonymous, account, wrtcsSessionId, endUrl } = options

  const userType = isAnonymous ? 'anonymous' : 'user'
  const userId = isAnonymous ? account : username
  const requestType = isAnonymous ? 'callMe' : 'callControl'

  // The common path for all call REST requests.
  let url = `${baseURL}/rest/version/${version}/${userType}/${userId}`

  if (endUrl) {
    url += `/${endUrl}`
  } else {
    url += `/${requestType}`
  }

  // If the REST request is for an existing call, the path includes its ID.
  if (wrtcsSessionId) {
    url += `/callSessions/${wrtcsSessionId}`
  }

  return url
}
